import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { UNRESOLVED_FACTS } from '../lib/company';
import { Eyebrow, Heading, Lead, Section } from './ui';

/**
 * Shared furniture for the Terms, Refund Policy and Privacy Policy.
 *
 * A legal document is a different reading task from a marketing page: people
 * arrive looking for one clause, not a narrative. So these render numbered
 * sections with stable anchors and a contents list beside them, and the prose
 * helpers keep the type small and dense rather than the airy sizes used on the
 * rest of the site.
 */

export interface LegalSection {
  /** Anchor fragment. Stable — external references and reviewers link to these. */
  id: string;
  title: string;
  body: ReactNode;
}

/**
 * Renders a date written as `YYYY-MM-DD`.
 *
 * Built from parts rather than `new Date('2026-08-17')`, which parses as UTC
 * midnight and therefore shows the previous day to anyone west of Greenwich.
 */
function formatDate(iso: string): string {
  const [year, month, day] = iso.split('-');
  return new Date(Number(year), Number(month) - 1, Number(day)).toLocaleDateString(
    undefined,
    { dateStyle: 'long' },
  );
}

/**
 * Names a fact that has not been supplied, in place, instead of inventing one.
 *
 * @see COMPANY in `../lib/company` for why these are gaps rather than guesses.
 */
export function Pending({ children }: { children: ReactNode }) {
  return (
    <span className="whitespace-nowrap rounded border border-dashed border-warn-600/60 bg-warn-600/10 px-1.5 py-0.5 text-warn-400">
      {children} — to be confirmed
    </span>
  );
}

/** A company fact, or a visible placeholder where one is missing. */
export function Fact({ value, label }: { value: string | null; label: string }) {
  return value ? <>{value}</> : <Pending>{label}</Pending>;
}

/**
 * Says plainly that these documents are not final, and what is missing.
 *
 * The wording is site-wide rather than per-document on purpose: the notice is
 * driven by one list of outstanding facts, and not every document happens to
 * cite every fact. Claiming "this document has gaps" on the one page that has
 * none would be its own small inaccuracy.
 *
 * Shown only while facts are outstanding, so it disappears on its own once
 * `COMPANY` is filled in — there is no second place to remember to remove it.
 */
export function ReadinessNotice() {
  if (UNRESOLVED_FACTS.length === 0) return null;

  return (
    <div
      role="note"
      className="mb-10 rounded-card border border-warn-600/40 bg-warn-600/10 p-5"
    >
      <h2 className="text-sm font-semibold text-warn-400">
        These documents are not final
      </h2>
      <p className="mt-2 max-w-3xl text-sm leading-relaxed text-hull-300">
        The details below have not been confirmed. Wherever they appear across
        these pages they are shown as gaps rather than filled with invented
        wording. They must be settled, and these documents reviewed by a
        qualified adviser, before this site is submitted for payment provider
        approval or takes a real payment.
      </p>
      <ul className="mt-4 space-y-2">
        {UNRESOLVED_FACTS.map((fact) => (
          <li key={fact.label} className="flex gap-2.5 text-sm text-hull-300">
            <span aria-hidden="true" className="mt-px text-warn-400">
              ·
            </span>
            <span>
              <strong className="font-semibold text-white">{fact.label}</strong> —{' '}
              {fact.note}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

export function LegalDocument({
  eyebrow,
  title,
  intro,
  updated,
  sections,
}: {
  eyebrow: string;
  title: string;
  intro: ReactNode;
  /** `YYYY-MM-DD`. Bump whenever the wording changes materially. */
  updated: string;
  sections: LegalSection[];
}) {
  return (
    <>
      <Section className="border-b border-hull-800">
        <Eyebrow>{eyebrow}</Eyebrow>
        <Heading level={1} className="text-white">
          {title}
        </Heading>
        <Lead className="mt-5 max-w-2xl">{intro}</Lead>
        <p className="mt-6 text-xs uppercase tracking-[0.14em] text-hull-500">
          Last updated {formatDate(updated)}
        </p>
      </Section>

      <Section>
        <ReadinessNotice />

        <div className="grid gap-10 lg:grid-cols-[210px_1fr] lg:gap-14">
          <nav
            aria-label="On this page"
            className="lg:sticky lg:top-24 lg:self-start"
          >
            <h2 className="mb-3 text-xs font-semibold uppercase tracking-[0.14em] text-hull-500">
              Contents
            </h2>
            <ol className="space-y-1.5">
              {sections.map((section, index) => (
                <li key={section.id}>
                  <a
                    href={`#${section.id}`}
                    className="text-sm text-hull-400 transition hover:text-surge-400"
                  >
                    <span className="tabular-nums text-hull-600">{index + 1}.</span>{' '}
                    {section.title}
                  </a>
                </li>
              ))}
            </ol>
          </nav>

          <div className="min-w-0 space-y-10">
            {sections.map((section, index) => (
              <section
                key={section.id}
                id={section.id}
                // Clears the sticky header when arriving via an anchor link.
                className="scroll-mt-24 border-t border-hull-800 pt-8 first:border-0 first:pt-0"
              >
                <h2 className="text-xl font-semibold text-white">
                  <span className="tabular-nums text-hull-500">{index + 1}.</span>{' '}
                  {section.title}
                </h2>
                {section.body}
              </section>
            ))}
          </div>
        </div>
      </Section>
    </>
  );
}

// --- Prose ----------------------------------------------------------------

export function P({ children }: { children: ReactNode }) {
  return <p className="mt-4 text-sm leading-relaxed text-hull-300">{children}</p>;
}

export function SubHeading({ children }: { children: ReactNode }) {
  return <h3 className="mt-7 text-base font-semibold text-white">{children}</h3>;
}

export function Bullets({ children }: { children: ReactNode }) {
  return <ul className="mt-4 space-y-2.5">{children}</ul>;
}

export function Bullet({ children }: { children: ReactNode }) {
  return (
    <li className="flex gap-2.5 text-sm leading-relaxed text-hull-300">
      <span aria-hidden="true" className="mt-px text-surge-400">
        ·
      </span>
      <span>{children}</span>
    </li>
  );
}

/** Pulled out of the flow: something a reader must not skim past. */
export function Callout({ children }: { children: ReactNode }) {
  return (
    <div className="mt-5 rounded-lg border border-hull-600 bg-hull-800/60 px-4 py-3.5 text-sm leading-relaxed text-hull-200">
      {children}
    </div>
  );
}

/**
 * A cross-reference to another clause in the same document.
 *
 * By anchor, never by clause number: a number goes stale the moment a section
 * is inserted above it, and nothing catches that.
 */
export function Anchor({ to, children }: { to: string; children: ReactNode }) {
  return (
    <a
      href={`#${to}`}
      className="text-surge-400 underline underline-offset-2 hover:text-surge-300"
    >
      {children}
    </a>
  );
}

/** A link to another page on this site. */
export function Internal({ to, children }: { to: string; children: ReactNode }) {
  return (
    <Link
      to={to}
      className="text-surge-400 underline underline-offset-2 hover:text-surge-300"
    >
      {children}
    </Link>
  );
}

/** An outbound link, opened in a new tab and stripped of `window.opener`. */
export function Outbound({ href, children }: { href: string; children: ReactNode }) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="text-surge-400 underline underline-offset-2 hover:text-surge-300"
    >
      {children}
    </a>
  );
}

export function Mail({ address }: { address: string }) {
  return (
    <a
      href={`mailto:${address}`}
      className="text-surge-400 underline underline-offset-2 hover:text-surge-300"
    >
      {address}
    </a>
  );
}
